import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { fetchDisabilityData } from '../api';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

// ── Program meta ──────────────────────────────────────────────────────────────

const PROGRAM_META = {
  rdsp: {
    number: 1,
    label: 'Registered Disability Savings Plan',
    abbr: 'RDSP',
    description:
      'A long-term savings plan for Canadians with disabilities. The federal government contributes through the Canada Disability Savings Grant (matching contributions up to $3,500/year) and the Canada Disability Savings Bond (up to $1,000/year for low-income holders), regardless of whether the holder contributes.',
    color: '#1D9E75',
    sourceLabel: 'ESDC Open Data — CDSP Statistics',
    sourceUrl: 'https://open.canada.ca/data/en/dataset/c2c23a59-7e4a-4bcd-8491-fcf6da3c4590',
  },
  dtc: {
    number: 2,
    label: 'Disability Tax Credit',
    abbr: 'DTC',
    description:
      'A non-refundable tax credit that reduces federal income tax for people with severe and prolonged physical or mental impairments. The DTC also acts as the gateway to the RDSP and the Canada Disability Benefit — applicants must hold a valid DTC certificate to access both programs.',
    color: '#378ADD',
    sourceLabel: 'CRA Open Data — DTC Statistics (T11)',
    sourceUrl: 'https://open.canada.ca/data/en/dataset/e8196226-031b-426f-9fdc-30b7cb272518',
  },
  cdb: {
    number: 3,
    label: 'Canada Disability Benefit',
    abbr: 'CDB',
    description:
      'A monthly income supplement for low-income working-age adults (18–64) with disabilities who hold a valid DTC certificate. Payments began July 2025 — up to $200/month, indexed to inflation and income-tested. The CDB is an income supplement, not a funding package for disability services.',
    color: '#C8873A',
    sourceLabel: null,
    sourceUrl: 'https://www.canada.ca/en/services/benefits/disability/canada-disability-benefit.html',
  },
};

// ── Formatters ────────────────────────────────────────────────────────────────

function fmt(n) {
  return Number(n).toLocaleString('en-CA');
}

function fmtShort(n) {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000)     return `$${(n / 1_000_000).toFixed(0)}M`;
  if (n >= 1_000)         return `${Math.round(n / 1_000)}k`;
  return String(n);
}

function fmtDollars(n) {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000)     return `$${(n / 1_000_000).toFixed(1)}M`;
  return `$${fmt(n)}`;
}

// ── Custom tooltips ───────────────────────────────────────────────────────────

const RdspTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1a1a2e',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 6,
      padding: '10px 14px',
      fontSize: 13,
      color: '#e8e4da',
      minWidth: 200,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
          <span style={{ color: 'rgba(232,228,218,0.7)', textTransform: 'capitalize' }}>{p.dataKey}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {p.dataKey === 'beneficiaries' ? fmt(p.value) : fmtDollars(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const DtcTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  return (
    <div style={{
      background: '#1a1a2e',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 6,
      padding: '10px 14px',
      fontSize: 13,
      color: '#e8e4da',
      minWidth: 200,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>{label}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
        <span style={{ color: 'rgba(232,228,218,0.7)' }}>Active certificates</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(row?.certificates_active)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 3 }}>
        <span style={{ color: 'rgba(232,228,218,0.7)' }}>Applications</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(row?.applications_total)}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
        <span style={{ color: 'rgba(232,228,218,0.7)' }}>Approval rate</span>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{row?.approval_rate_pct}%</span>
      </div>
    </div>
  );
};

// ── Stat pill ─────────────────────────────────────────────────────────────────

function StatPill({ label, value }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="kicker text-ink-500" style={{ fontSize: 11 }}>{label}</span>
      <span className="font-serif text-ink-700" style={{ fontSize: 28, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Disability() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    fetchDisabilityData()
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  const rdsp = data?.rdsp;
  const dtc  = data?.dtc;

  // RDSP chart — beneficiary count history
  const rdspChartData = rdsp?.history?.map(h => ({
    year: String(h.year),
    beneficiaries: h.beneficiaries,
    grant: h.grant_cad,
    bond:  h.bond_cad,
  })) ?? [];

  // DTC chart — certificate history
  const dtcChartData = dtc?.history?.map(h => ({
    year: String(h.year),
    certificates_active: h.certificates_active,
    applications_total:  h.applications_total,
    approval_rate_pct:   h.approval_rate_pct,
  })) ?? [];

  const rdspLatest = rdsp?.latest;
  const dtcLatest  = dtc?.latest;

  return (
    <div className="min-h-screen bg-cream-100 text-ink-700">
      <Header />

      {/* ── Banner ── */}
      <section className="bg-ink-800 text-cream-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[13px] text-cream-100/65 hover:text-copper-500 mb-6 transition"
          >
            <ArrowLeft size={14} /> Home
          </Link>
          <div className="kicker text-copper-500 mb-4">Disability Programs</div>
          <h1 className="font-serif text-[56px] md:text-[64px] leading-[0.98] tracking-tight">
            Federal disability<br />program statistics
          </h1>
          <p className="mt-6 text-[16px] text-cream-100/75 max-w-2xl leading-relaxed">
            Annual data on Canada's three core federal disability programs — the RDSP, DTC, and CDB.
            Statistics are drawn from ESDC and CRA open data published on the Open Government Portal.
          </p>

          {/* Program pill nav */}
          <div className="flex flex-wrap gap-3 mt-10">
            {Object.entries(PROGRAM_META).map(([key, meta]) => (
              <a
                key={key}
                href={`#${key}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cream-100/20 text-[13px] text-cream-100/70 hover:border-copper-500 hover:text-copper-400 transition"
              >
                <span
                  className="w-2 h-2 rounded-full inline-block"
                  style={{ background: meta.color }}
                />
                {meta.abbr}
              </a>
            ))}
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-16">

        {/* Loading */}
        {loading && (
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-8 animate-pulse">
            <div className="h-4 w-40 bg-ink-200 rounded mb-6" />
            <div className="h-20 w-64 bg-ink-200 rounded" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 mb-8 text-ink-500">
            Could not load live data — please try again shortly.
          </div>
        )}

        {/* ════════════════════════════════════════════════════
            SECTION 1 — RDSP
        ════════════════════════════════════════════════════ */}
        <div id="rdsp" className="scroll-mt-8">

          {/* Hero stat */}
          {rdspLatest && (
            <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-4"
                 style={{ borderLeft: `4px solid ${PROGRAM_META.rdsp.color}` }}>
              <div className="kicker text-copper-600 mb-6">
                RDSP beneficiaries · {rdspLatest.year}
              </div>
              <div className="font-serif text-[72px] md:text-[100px] leading-[0.9] tracking-tight text-ink-800 tabular">
                {fmt(rdspLatest.beneficiaries)}
              </div>
              {/* Financial sub-stats */}
              <div className="flex flex-wrap gap-10 mt-8 pt-8 border-t border-ink-300/40">
                <StatPill
                  label="Gov't grants paid"
                  value={fmtDollars(rdspLatest.financials.grant_cad)}
                />
                <StatPill
                  label="Gov't bonds paid"
                  value={fmtDollars(rdspLatest.financials.bond_cad)}
                />
                <StatPill
                  label="Private contributions"
                  value={fmtDollars(rdspLatest.financials.contribution_cad)}
                />
              </div>
            </div>
          )}

          {/* RDSP chart */}
          {rdspChartData.length > 0 && (
            <div className="bg-ink-800 rounded-sm p-8 md:p-10 mb-4">
              <h2 className="font-serif text-[22px] text-cream-100 mb-2">
                RDSP beneficiaries over time
              </h2>
              <p className="text-[13px] text-cream-100/50 mb-8">
                National total active RDSP holders, 2015 – {rdspChartData.at(-1)?.year}
              </p>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={rdspChartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="rdspGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={PROGRAM_META.rdsp.color} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={PROGRAM_META.rdsp.color} stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="year"
                    tick={{ fill: 'rgba(232,228,218,0.6)', fontSize: 13 }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tickFormatter={n => `${Math.round(n / 1000)}k`}
                    tick={{ fill: 'rgba(232,228,218,0.4)', fontSize: 12 }}
                    axisLine={false} tickLine={false} width={48}
                  />
                  <Tooltip content={<RdspTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.08)', strokeWidth: 1 }} />
                  <Area
                    type="monotone"
                    dataKey="beneficiaries"
                    stroke={PROGRAM_META.rdsp.color}
                    strokeWidth={2}
                    fill="url(#rdspGrad)"
                    dot={false}
                    activeDot={{ r: 4, fill: PROGRAM_META.rdsp.color, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* RDSP gov't contributions bar chart */}
          {rdspChartData.length > 0 && (
            <div className="bg-ink-800 rounded-sm p-8 md:p-10 mb-8">
              <h2 className="font-serif text-[22px] text-cream-100 mb-2">
                Government contributions to RDSPs
              </h2>
              <p className="text-[13px] text-cream-100/50 mb-8">Annual grants and bonds paid by the federal government</p>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={rdspChartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }} barCategoryGap="28%">
                  <XAxis
                    dataKey="year"
                    tick={{ fill: 'rgba(232,228,218,0.6)', fontSize: 13 }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tickFormatter={n => `$${Math.round(n / 1_000_000)}M`}
                    tick={{ fill: 'rgba(232,228,218,0.4)', fontSize: 12 }}
                    axisLine={false} tickLine={false} width={56}
                  />
                  <Tooltip content={<RdspTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                  <Bar dataKey="grant" name="Grant" stackId="a" fill={PROGRAM_META.rdsp.color} radius={[0,0,0,0]} />
                  <Bar dataKey="bond"  name="Bond"  stackId="a" fill="#6BCBA8"                 radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex gap-6 mt-5">
                <div className="flex items-center gap-2">
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: PROGRAM_META.rdsp.color, display: 'inline-block' }} />
                  <span className="text-[12px] text-cream-100/60">Canada Disability Savings Grant</span>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: '#6BCBA8', display: 'inline-block' }} />
                  <span className="text-[12px] text-cream-100/60">Canada Disability Savings Bond</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════
            SECTION 2 — DTC
        ════════════════════════════════════════════════════ */}
        <div id="dtc" className="scroll-mt-8">

          {dtcLatest && (
            <div className="bg-cream-50 border border-ink-300/60 rounded-sm p-12 md:p-16 mb-4"
                 style={{ borderLeft: `4px solid ${PROGRAM_META.dtc.color}` }}>
              <div className="kicker text-copper-600 mb-6">
                DTC certificates active · {dtcLatest.year}
              </div>
              <div className="font-serif text-[72px] md:text-[100px] leading-[0.9] tracking-tight text-ink-800 tabular">
                {fmt(dtcLatest.certificates_active)}
              </div>
              <div className="flex flex-wrap gap-10 mt-8 pt-8 border-t border-ink-300/40">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span className="kicker text-ink-400" style={{ fontSize: 11 }}>Applications received</span>
                  <span className="font-serif text-[28px] leading-none text-ink-700 tabular">
                    {fmt(dtcLatest.applications_total)}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span className="kicker text-ink-400" style={{ fontSize: 11 }}>Approved</span>
                  <span className="font-serif text-[28px] leading-none text-ink-700 tabular">
                    {fmt(dtcLatest.approved)}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span className="kicker text-ink-400" style={{ fontSize: 11 }}>Approval rate</span>
                  <span className="font-serif text-[28px] leading-none text-ink-700 tabular">
                    {dtcLatest.approval_rate_pct}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* DTC certificate area chart */}
          {dtcChartData.length > 0 && (
            <div className="bg-ink-800 rounded-sm p-8 md:p-10 mb-8">
              <h2 className="font-serif text-[22px] text-cream-100 mb-2">
                DTC certificates &amp; applications over time
              </h2>
              <p className="text-[13px] text-cream-100/50 mb-8">
                Annual DTC applications received vs. active certificates approved, 2014 – {dtcChartData.at(-1)?.year}
              </p>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={dtcChartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="dtcGradApps" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={PROGRAM_META.dtc.color} stopOpacity={0.15} />
                      <stop offset="95%" stopColor={PROGRAM_META.dtc.color} stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="dtcGradCerts" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={PROGRAM_META.dtc.color} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={PROGRAM_META.dtc.color} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="year"
                    tick={{ fill: 'rgba(232,228,218,0.6)', fontSize: 13 }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tickFormatter={n => `${Math.round(n / 1000)}k`}
                    tick={{ fill: 'rgba(232,228,218,0.4)', fontSize: 12 }}
                    axisLine={false} tickLine={false} width={48}
                  />
                  <Tooltip content={<DtcTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.08)', strokeWidth: 1 }} />
                  <Area
                    type="monotone"
                    dataKey="applications_total"
                    stroke={PROGRAM_META.dtc.color}
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                    fill="url(#dtcGradApps)"
                    dot={false}
                    activeDot={{ r: 3, fill: PROGRAM_META.dtc.color, strokeWidth: 0 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="certificates_active"
                    stroke={PROGRAM_META.dtc.color}
                    strokeWidth={2}
                    fill="url(#dtcGradCerts)"
                    dot={false}
                    activeDot={{ r: 4, fill: PROGRAM_META.dtc.color, strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
              <div className="flex gap-6 mt-5">
                <div className="flex items-center gap-2">
                  <span style={{ width: 18, height: 2, background: PROGRAM_META.dtc.color, display: 'inline-block', borderRadius: 1 }} />
                  <span className="text-[12px] text-cream-100/60">Active certificates</span>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ width: 18, height: 2, background: PROGRAM_META.dtc.color, display: 'inline-block', borderRadius: 1, opacity: 0.5 }} />
                  <span className="text-[12px] text-cream-100/60">Applications received</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ════════════════════════════════════════════════════
            SECTION 3 — Program cards
        ════════════════════════════════════════════════════ */}
        <h2 className="font-serif text-[28px] text-ink-800 mb-6 mt-4">About each program</h2>
        <div className="flex flex-col gap-4 mb-12">
          {Object.entries(PROGRAM_META).map(([key, meta]) => (
            <div
              key={key}
              id={key === 'cdb' ? 'cdb' : undefined}
              className="bg-cream-50 border border-ink-300/60 rounded-sm p-8 scroll-mt-8"
              style={{ borderLeft: `4px solid ${meta.color}` }}
            >
              <div className="flex items-start gap-4">
                <span
                  className="text-[11px] font-semibold rounded px-2 py-0.5 mt-0.5 shrink-0"
                  style={{ color: meta.color, background: meta.color + '18' }}
                >
                  {meta.number}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 mb-3">
                    <span className="font-semibold text-[15px] text-ink-800">{meta.label}</span>
                    <span
                      className="text-[13px] font-semibold"
                      style={{ color: meta.color }}
                    >
                      {meta.abbr}
                    </span>
                  </div>
                  <p className="text-[13px] text-ink-500 leading-relaxed mb-3">
                    {meta.description}
                  </p>
                  {key === 'cdb' && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded text-[12px] font-medium"
                         style={{ background: meta.color + '15', color: meta.color }}>
                      ⚠ Open data not yet available — program launched July 2025
                    </div>
                  )}
                  {meta.sourceLabel && (
                    <a
                      href={meta.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[12px] text-ink-400 hover:text-copper-600 transition"
                    >
                      {meta.sourceLabel} <ExternalLink size={10} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ════════════════════════════════════════════════════
            SECTION 4 — Historical tables
        ════════════════════════════════════════════════════ */}

        {/* RDSP historical table */}
        {rdsp?.history?.length > 0 && (
          <>
            <h2 className="font-serif text-[28px] text-ink-800 mb-6 mt-4">RDSP — historical data</h2>
            <div className="overflow-x-auto mb-12">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-ink-300/60 text-ink-400 text-left">
                    <th className="pb-3 font-medium">Year</th>
                    <th className="pb-3 font-medium text-right">Beneficiaries</th>
                    <th className="pb-3 font-medium text-right">Grants paid</th>
                    <th className="pb-3 font-medium text-right">Bonds paid</th>
                    <th className="pb-3 font-medium text-right">Contributions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...rdsp.history].reverse().map(row => (
                    <tr
                      key={row.year}
                      className={`border-b border-ink-200/60 ${row.year === rdspLatest?.year ? 'font-semibold text-ink-800' : 'text-ink-600'}`}
                    >
                      <td className="py-2.5">{row.year}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.beneficiaries)}</td>
                      <td className="py-2.5 text-right tabular">{fmtDollars(row.grant_cad)}</td>
                      <td className="py-2.5 text-right tabular">{fmtDollars(row.bond_cad)}</td>
                      <td className="py-2.5 text-right tabular">{fmtDollars(row.contribution_cad)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* DTC historical table */}
        {dtc?.history?.length > 0 && (
          <>
            <h2 className="font-serif text-[28px] text-ink-800 mb-6">DTC — historical data</h2>
            <div className="overflow-x-auto mb-12">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-ink-300/60 text-ink-400 text-left">
                    <th className="pb-3 font-medium">Year</th>
                    <th className="pb-3 font-medium text-right">Active certificates</th>
                    <th className="pb-3 font-medium text-right">Applications</th>
                    <th className="pb-3 font-medium text-right">Approved</th>
                    <th className="pb-3 font-medium text-right">Rejected</th>
                    <th className="pb-3 font-medium text-right">Approval rate</th>
                  </tr>
                </thead>
                <tbody>
                  {[...dtc.history].reverse().map(row => (
                    <tr
                      key={row.year}
                      className={`border-b border-ink-200/60 ${row.year === dtcLatest?.year ? 'font-semibold text-ink-800' : 'text-ink-600'}`}
                    >
                      <td className="py-2.5">{row.year}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.certificates_active)}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.applications_total)}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.approved)}</td>
                      <td className="py-2.5 text-right tabular">{fmt(row.rejected)}</td>
                      <td className="py-2.5 text-right tabular">{row.approval_rate_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ── Sources footer ── */}
        <div className="mt-10 text-[14px] text-ink-500 leading-relaxed">
          <strong className="text-ink-700">Sources:</strong>{' '}
          Employment and Social Development Canada (ESDC) — Canada Disability Savings Program Statistics;
          Canada Revenue Agency (CRA) — Disability Tax Credit Statistics.{' '}
          <a
            href="https://open.canada.ca/data/en/dataset/c2c23a59-7e4a-4bcd-8491-fcf6da3c4590"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-copper-600 hover:text-copper-500 ml-1"
          >
            CDSP Open Data <ExternalLink size={11} />
          </a>
          {' ·'}
          <a
            href="https://open.canada.ca/data/en/dataset/e8196226-031b-426f-9fdc-30b7cb272518"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-copper-600 hover:text-copper-500 ml-1"
          >
            DTC Open Data <ExternalLink size={11} />
          </a>
        </div>
        <div className="mt-4 text-[13px] text-ink-400 italic max-w-2xl">
          RDSP data covers 2015–2024. DTC data covers 2014–2023. Both datasets are updated annually
          by their respective federal departments.
        </div>
      </main>

      <Footer />
    </div>
  );
}
